/**
 * Reusable, searchable DIN 276 category picker.
 *
 * A Popover + Command (cmdk) list over the DIN 276 metadata (loaded from the
 * cached `getDIN276Metadata` endpoint) that lets the user search by typing
 * either a code or a name (the option text is `<code> — <name>`). `value` may
 * be a code OR a name; it is resolved to the canonical code. Set
 * `includeEmptyOption` to get a clearable "all" entry — useful when the picker
 * is used as a table filter rather than a required field.
 */

import { useEffect, useMemo, useState } from "react";
import { Check, ChevronDown } from "lucide-react";

import { cn } from "@/lib/utils";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { userAPI } from "../UserAPI";
import { resolveDIN276CategoryCode } from "../utilities/din276Category";

interface Properties {
  /** Currently selected category — a code or a name; resolved to a code. */
  value?: string;
  /** Called with the selected code, or undefined when the empty option is picked. */
  onChange: (code: string | undefined) => void;
  placeholder?: string;
  /** Prepend a clearable "all / none" option (e.g. for filters). */
  includeEmptyOption?: boolean;
  emptyOptionText?: string;
  width?: number;
  className?: string;
  disabled?: boolean;
}

/**
 * Match scorer for the category search: the typed string must be a case-insensitive
 * PREFIX of at least one space-separated fragment of the option (skipping dash-only
 * fragments like the "—" separator). E.g. "wasser" matches "412 — Wasseranlagen".
 */
const fragmentPrefixFilter = (value: string, search: string): number => {
  const s = search.trim().toLowerCase();
  if (!s) return 1;
  for (const frag of value.toLowerCase().split(/\s+/)) {
    if (!frag || /^[-—]+$/.test(frag)) continue;
    if (frag.startsWith(s)) return 1;
  }
  return 0;
};

/** Sort DIN 276 codes numerically segment-by-segment (411.10 before 411.2 → false). */
const sortByCode = (a: any, b: any) => {
  const xs = String(a.code).split(".");
  const ys = String(b.code).split(".");
  const segments = Math.max(xs.length, ys.length);
  for (let i = 0; i < segments; i++) {
    const xv = parseInt(xs[i] ?? "0", 10) || 0;
    const yv = parseInt(ys[i] ?? "0", 10) || 0;
    if (xv !== yv) return xv - yv;
  }
  return 0;
};

const EMPTY_KEY = "__all__";

export default function DIN276CategoryPicker(properties: Properties) {
  const [categories, setCategories] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data: any[] = await userAPI.getDIN276Metadata();
        if (!cancelled && Array.isArray(data)) setCategories(data);
      } catch {
        /* metadata is best-effort */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const options = useMemo(() => {
    const base = [...categories].sort(sortByCode).map((c: any) => ({
      key: c.code,
      text: c.name ? `${c.code} — ${c.name}` : c.code,
    }));
    return properties.includeEmptyOption
      ? [{ key: EMPTY_KEY, text: properties.emptyOptionText || "—" }, ...base]
      : base;
  }, [categories, properties.includeEmptyOption, properties.emptyOptionText]);

  // value may be a code or a name → resolve to the canonical code
  const selectedKey = useMemo(() => {
    const needle = String(properties.value ?? "").trim();
    if (!needle) {
      return properties.includeEmptyOption ? EMPTY_KEY : undefined;
    }
    return resolveDIN276CategoryCode(needle, categories);
  }, [properties.value, properties.includeEmptyOption, categories]);

  const width = properties.width ?? 520;

  // Solange die Metadaten fehlen, gibt es zum Code noch keinen Text — dann
  // steht der Code selbst da, statt die Auswahl scheinbar zu verlieren.
  const selectedDisplay =
    selectedKey == null
      ? ""
      : options.find((option) => option.key === selectedKey)?.text ??
        String(selectedKey);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {/* Die Breite kam bisher als Inline-Wert aus der Prop; `max-w-full` hält
          das Feld auf schmalen Viewports trotzdem im Container. */}
      <PopoverTrigger
        role="combobox"
        aria-expanded={open}
        disabled={properties.disabled}
        className={cn(
          "flex h-9 max-w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm outline-none transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 max-sm:h-11",
          properties.className,
        )}
        style={{ width }}
      >
        <span
          className={cn(
            "line-clamp-1 text-left",
            !selectedDisplay && "text-muted-foreground",
          )}
        >
          {selectedDisplay || properties.placeholder || "—"}
        </span>
        <ChevronDown aria-hidden="true" className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command filter={fragmentPrefixFilter}>
          <CommandInput
            placeholder={properties.placeholder ?? "Search…"}
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>No options</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={String(option.key)}
                  value={`${option.text} ${String(option.key)}`}
                  onSelect={() => {
                    properties.onChange(
                      option.key === EMPTY_KEY ? undefined : option.key,
                    );
                    setOpen(false);
                  }}
                >
                  <Check
                    aria-hidden="true"
                    className={cn(
                      "mr-2 h-4 w-4",
                      option.key === selectedKey ? "opacity-100" : "opacity-0",
                    )}
                  />
                  <span className="flex-1 truncate">{option.text}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
